/**
 * ============================================================
 * GuideGuru — Panchang Data Fetcher
 * Fetches panchang data via the proxy endpoint (App Proxy or Vercel)
 * so the real API URL never appears in the browser Network panel.
 *
 * Usage: Include this JS in the panchang-today.liquid section.
 * The section sets `window.__GUIDEGURU_PROXY_URL` before loading this.
 * ============================================================
 */

(function () {
  'use strict';

  // --- Configuration ---
  const PROXY_BASE = window.__GUIDEGURU_PROXY_URL || '/apps/guideguru';
  const DEFAULT_LAT = 28.6448;
  const DEFAULT_LON = 77.2167;

  /**
   * Query DOM elements FRESH each time (never cache references).
   * This avoids stale references when Shopify re-renders sections.
   */
  function getContainer() {
    return document.querySelector('[data-guideguru-panchang]') || document.getElementById('panchang-box');
  }

  function getLoading(container) {
    return container ? container.querySelector('.panchang-loading') : null;
  }

  function getContent(container) {
    return container ? container.querySelector('.panchang-content') : null;
  }

  function getError(container) {
    return container ? container.querySelector('.panchang-error') : null;
  }

  /**
   * Format today's date as YYYY-MM-DD
   */
  function getTodayDate() {
    var now = new Date();
    var y = now.getFullYear();
    var m = String(now.getMonth() + 1).padStart(2, '0');
    var d = String(now.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + d;
  }

  /**
   * Format today's date for display: "Friday, 22 May 2026"
   */
  function getDisplayDate() {
    var now = new Date();
    return now.toLocaleDateString('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
  }

  /**
   * Show loading state — queries DOM fresh
   */
  function showLoading() {
    var container = getContainer();
    if (!container) return;
    var loading = getLoading(container);
    var content = getContent(container);
    var error   = getError(container);
    if (loading) loading.style.display = 'block';
    if (content) content.style.display = 'none';
    if (error)   error.style.display   = 'none';
  }

  /**
   * Show error state — queries DOM fresh
   */
  function showError(message) {
    var container = getContainer();
    if (!container) return;
    var loading = getLoading(container);
    var content = getContent(container);
    var error   = getError(container);
    if (loading) loading.style.display = 'none';
    if (content) content.style.display = 'none';
    if (error) {
      error.style.display = 'block';
      var msgEl = error.querySelector('p');
      if (msgEl) msgEl.textContent = message || 'Failed to load panchang data.';
    }
  }

  /**
   * Show content and populate data — queries DOM fresh
   */
  function showContent(data) {
    var container = getContainer();
    if (!container) {
      console.error('GuideGuru Panchang: #panchang-box or [data-guideguru-panchang] not found in DOM');
      return;
    }

    var loading = getLoading(container);
    var content = getContent(container);
    var error   = getError(container);

    // Hide loading & error, show content
    if (loading) loading.style.display = 'none';
    if (error)   error.style.display   = 'none';
    if (content) {
      content.style.display = '';
      content.removeAttribute('style');
    }

    // --- Populate all fields (scoped to container) ---
    setEl(container, '#panchang-display-date', getDisplayDate());
    setEl(container, '#sunrise-data', data.sunrise || '-');
    setEl(container, '#sunset-data', data.sunset || '-');
    setEl(container, '#moonrise-data', data.moonrise || '-');
    setEl(container, '#moonset-data', data.moonset || '-');

    // Month
    if (data.hindu_maah) {
      setEl(container, '#amanta-data', 'Amanta: ' + (data.hindu_maah.amanta || '-'));
      setEl(container, '#purnimanta-data', 'Purnimanta: ' + (data.hindu_maah.purnimanta || '-'));
    }

    // Samvat
    setEl(container, '#vikram-samvat-data',
      'Vikram: ' + (data.vikram_samvat || '-') + ' - ' + (data.vkram_samvat_name || ''));
    setEl(container, '#shakasamvat-data',
      'Shaka: ' + (data.shaka_samvat || '-') + ' - ' + (data.shaka_samvat_name || ''));

    // Tithi
    if (data.tithi && data.tithi.details) {
      setElHtml(container, '#tithiname-data',
        '<b>TITHI</b>: ' + esc(data.tithi.details.tithi_name || '-'));
      var tithiTill = data.tithi.end_time
        ? data.tithi.end_time.hour + ':' + data.tithi.end_time.minute + ':' + data.tithi.end_time.second
        : '-';
      setElHtml(container, '#tithitill-data', '<b>Till</b>: ' + esc(tithiTill));
    }

    // Nakshatra
    if (data.nakshatra && data.nakshatra.details) {
      setElHtml(container, '#nakshatra-data',
        '<b>Nakshatra</b>: ' + esc(data.nakshatra.details.nak_name || '-'));
      var nakTill = data.nakshatra.end_time
        ? data.nakshatra.end_time.hour + ':' + data.nakshatra.end_time.minute + ':' + data.nakshatra.end_time.second
        : '-';
      setElHtml(container, '#nakshatra-till-data', '<b>till</b>: ' + esc(nakTill));
    }

    // Yog
    if (data.yog && data.yog.details) {
      setElHtml(container, '#yog-data',
        '<b>Yog</b>: ' + esc(data.yog.details.yog_name || '-'));
      var yogTill = data.yog.end_time
        ? data.yog.end_time.hour + ':' + data.yog.end_time.minute + ':' + data.yog.end_time.second
        : '-';
      setElHtml(container, '#yog-till-data', '<b>till</b>: ' + esc(yogTill));
    }

    // Karan
    if (data.karan && data.karan.details) {
      setElHtml(container, '#karan-name-data',
        '<b>karan</b>: ' + esc(data.karan.details.karan_name || '-'));
      var karanTill = data.karan.end_time
        ? data.karan.end_time.hour + ':' + data.karan.end_time.minute + ':' + data.karan.end_time.second
        : '-';
      setElHtml(container, '#karan-till-data', '<b>till</b>: ' + esc(karanTill));
    }

    // Abhijit Muhurta
    if (data.abhijit_muhurta) {
      setEl(container, '#abhijit_muhurta_start_hm', data.abhijit_muhurta.start || '-');
      setEl(container, '#abhijit_muhurta_end_hm', data.abhijit_muhurta.end || '-');
    }

    // Rahu Kaal
    if (data.rahukaal) {
      setEl(container, '#rahukaal_start_hm', data.rahukaal.start || '-');
      setEl(container, '#rahukaal_end_hm', data.rahukaal.end || '-');
    }

    console.log('GuideGuru Panchang: data loaded successfully');
  }

  /**
   * Set textContent of an element found by selector within container.
   */
  function setEl(container, selector, text) {
    var el = container.querySelector(selector);
    if (el) el.textContent = text;
  }

  /**
   * Set innerHTML of an element found by selector within container.
   */
  function setElHtml(container, selector, html) {
    var el = container.querySelector(selector);
    if (el) el.innerHTML = html;
  }

  function esc(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Fetch panchang data from the proxy.
   */
  function fetchPanchang(lat, lon) {
    showLoading();

    var date = getTodayDate();
    var url = PROXY_BASE + '/api/panchang?date=' + date + '&lat=' + lat + '&lon=' + lon;

    fetch(url)
      .then(function (resp) {
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        return resp.json();
      })
      .then(function (json) {
        if (json.ok && json.data) {
          showContent(json.data);
        } else if (json.data) {
          showContent(json.data);
        } else {
          showError('Invalid response from server.');
        }
      })
      .catch(function (err) {
        console.error('GuideGuru Panchang: fetch error', err);
        showError('Unable to load panchang data. Please try again.');
      });
  }

  /**
   * Try to get user's geolocation, fall back to defaults.
   */
  function initWithGeolocation() {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          fetchPanchang(pos.coords.latitude.toFixed(6), pos.coords.longitude.toFixed(6));
        },
        function () {
          fetchPanchang(DEFAULT_LAT, DEFAULT_LON);
        },
        { timeout: 5000, maximumAge: 300000 }
      );
    } else {
      fetchPanchang(DEFAULT_LAT, DEFAULT_LON);
    }
  }

  /**
   * Initialize: wait for DOM, find container, attach retry, start fetch.
   */
  function init() {
    var container = getContainer();
    if (!container) {
      console.warn('GuideGuru Panchang: no panchang container found on this page.');
      return;
    }

    // Retry button
    var retryBtn = container.querySelector('.panchang-retry-btn');
    if (retryBtn) {
      retryBtn.addEventListener('click', function () {
        initWithGeolocation();
      });
    }

    initWithGeolocation();
  }

  // --- Boot ---
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
